/**
 * Comments on a feed card (t157u): the newest few messages of the item's entity thread, rendered with the shared chat
 * MessageItem (reactions, reply in thread, the full action menu, mentions, attachments, link previews, edited markers)
 * and the shared Composer. "View all N comments" expands inline; realtime keeps it in sync with the item's own page.
 * t174u: the item's own events (status changes, assignments) show inline between the comments, in time order, as the
 * same quiet system lines as the item's discussion ("Euan Whitfield changed the status from In progress to Resolved").
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessageCircle } from 'lucide-react';
import type { EntityEventMeta, Message } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useMembers, useWorkspaceId } from '@/lib/hooks';
import { messageStore, useConversationState } from '@/lib/messageStore';
import { Spinner } from '@/components/ui';
import { Composer } from '@/features/chat/Composer';
import { MessageItem, PendingItem } from '@/features/chat/MessageItem';
import { ThreadPrefsProvider } from '@/features/chat/InlineThread';
import { BubbleRows, SystemLine, useEntityThread } from './EntityThread';
import { useLiveEntityConversation, useThreadHost } from './liveConversation';

export const PREVIEW_COMMENTS = 3;

/** Comment counts without opening (or creating) the thread. */
export function useCommentCount(type: string, id: string, known?: number) {
  const ws = useWorkspaceId();
  const q = useQuery({ queryKey: ['entity-thread-count', ws, type, id], queryFn: () => api.entityThreadCounts(ws!, type, [id]).then(r => r.counts[id] ?? 0), enabled: Boolean(ws) && known === undefined, staleTime: 30_000 });
  return known ?? q.data;
}

/** A status or resolution change (issue status, a task completed): the line that says where the item stands. */
const isStatus = (m: Message) => { const x = (m.metadata ?? {}) as { field?: string; event?: string }; return x.field === 'status' || /\.(status|resolved|completed)$/.test(x.event ?? ''); };
const isEvent = (m: Message) => m.kind === 'system' && !(m.metadata as EntityEventMeta | null)?.subtle;
/**
 * What the preview shows, oldest first: the newest `max` comments (all when expanded), plus the item's events from the
 * first of those on (all of them, between the comments) and the latest status change, however old. With no comments yet, the newest `max` events. Quiet lines
 * (RSVPs) stay in the full discussion.
 */
export function previewRows(messages: Message[], max: number, all: boolean): Message[] {
  const top = messages.filter(m => !m.parent_id);
  const roots = top.filter(m => m.kind === 'user');
  const events = top.filter(isEvent);
  if (!roots.length) return all ? events : events.slice(-max);
  const shown = new Set(all ? roots : roots.slice(-max));
  const from = all ? '' : [...shown][0].created_at;
  // t174u: the latest status change (e.g. resolved) always shows, in its place just above the comments after it.
  const status = events.filter(isStatus).at(-1);
  return top.filter(m => shown.has(m) || (isEvent(m) && m.created_at >= from) || m === status);
}

function OpenThread({ type, entityId, max, focus, hideComposer, bubbles }: { type: string; entityId: string; max: number; focus: boolean; hideComposer?: boolean; bubbles?: boolean }) {
  const info = useEntityThread(type, entityId);
  const convId = info.data?.conversation_id;
  const state = useConversationState(convId);
  const [all, setAll] = useState(false);
  useLiveEntityConversation(convId);
  const add = useThreadHost(s => s.add);
  const roots = useMemo(() => state.messages.filter(m => !m.parent_id && m.kind === 'user'), [state.messages]);
  useEffect(() => { if (convId && roots.length) add(convId, roots.map(m => m.id), Boolean(info.data?.can_post)); }, [convId, roots, add, info.data?.can_post]);
  const { byId } = useMembers();
  // Bubbles (a phone detail screen): the whole discussion, quiet lines folded, as the app; the feed shows a preview.
  const rows = useMemo(() => (bubbles ? state.messages.filter(m => !m.parent_id) : previewRows(state.messages, max, all)), [state.messages, max, all, bubbles]);
  const hidden = all || bubbles ? 0 : roots.length - Math.min(roots.length, max);
  if (info.isLoading || (convId && state.status !== 'ready' && !roots.length)) return <div className="py-2"><Spinner /></div>;
  if (!convId) return null;
  if (bubbles) return (
    // t359u parity: a phone detail screen's comments as the app draws them (apps/mobile EntityThread with a header).
    <div data-testid="feed-comments" className="-mx-4 py-1">
      {state.nextBefore !== null && (
        <button type="button" className="mx-4 mb-1 text-xs font-semibold text-slate-600 dark:text-slate-300" data-testid="view-all-comments"
          onClick={() => void messageStore.loadOlder(convId)}>Show earlier comments</button>
      )}
      <BubbleRows messages={rows} />
      {state.pending.filter(p => !p.parent_id).map(p => <PendingItem key={p.client_message_id} p={p} conversationId={convId} />)}
    </div>
  );
  return (
    <ThreadPrefsProvider conversationId={convId} canReply={Boolean(info.data?.can_post)} canReact={info.data?.can_react !== false}>
      {/* t188u: in comments the text is regular grey so the commenter's name (bold, near-black) stands apart. */}
      <div data-testid="feed-comments" className="mt-3 border-t border-slate-100 pt-2 dark:border-slate-800 [&_[data-message-body]]:text-slate-600 dark:[&_[data-message-body]]:text-slate-300">
        {(hidden > 0 || (all && state.nextBefore !== null)) && (
          <button type="button" className="mb-1 px-1 text-xs font-semibold text-slate-600 hover:underline dark:text-slate-300" data-testid="view-all-comments"
            onClick={() => { setAll(true); if (state.nextBefore !== null) void messageStore.loadOlder(convId); }}>
            {all ? 'Show earlier comments' : `View all ${roots.length}${state.nextBefore !== null ? '+' : ''} comments`}
          </button>
        )}
        <div className="-mx-4">
          {rows.map((m, i) => {
            if (m.kind === 'system') return <SystemLine key={m.id} m={m} actor={byId.get(m.sender_id)?.display_name ?? 'Someone'} />;
            const prev = rows[i - 1];
            return <MessageItem key={m.id} m={m} comment compact={Boolean(prev) && prev.kind === 'user' && prev.sender_id === m.sender_id && Date.parse(m.created_at) - Date.parse(prev.created_at) < 5 * 60_000} />;
          })}
          {state.pending.filter(p => !p.parent_id).map(p => <PendingItem key={p.client_message_id} p={p} conversationId={convId} />)}
        </div>
        {info.data?.can_post && !hideComposer && <div className="mt-1 [&_form]:!p-0"><Composer key={convId} conversationId={convId} placeholder="Write a comment…" autoFocus={focus} /></div>}
      </div>
    </ThreadPrefsProvider>
  );
}

/** Shows the comments when there are some (or once you press Comment); otherwise just the Comment button. */
export function CommentsPreview({ type, entityId, count, open: forceOpen, max = PREVIEW_COMMENTS, hideButton, autoFocus, hideComposer, bubbles }: {
  type: string; entityId: string; count?: number; open?: boolean; max?: number; hideButton?: boolean; autoFocus?: boolean;
  /** t202u: the sheet pins the composer (PinnedComposer) under the list instead. */ hideComposer?: boolean;
  /** t359u parity: phone detail screens draw the comments as chat bubbles, like the app. */ bubbles?: boolean;
}) {
  const n = useCommentCount(type, entityId, count);
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(false);
  const show = forceOpen || open || (n ?? 0) > 0;
  return (
    <>
      {!hideButton && !show && (
        <button type="button" onClick={() => { setOpen(true); setFocus(true); }} data-testid="comment-button"
          className="mt-2 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"><MessageCircle className="h-4 w-4" />Comment</button>
      )}
      {show && <OpenThread type={type} entityId={entityId} max={max} focus={focus || Boolean(autoFocus)} hideComposer={hideComposer} bubbles={bubbles} />}
    </>
  );
}

/** t202u: the comment box of an item's thread, for a sheet's pinned footer (the list scrolls above it). */
export function PinnedComposer({ type, entityId, placeholder = 'Write a comment…', phone = false }: { type: string; entityId: string; placeholder?: string;
  /** t359u parity: the phone detail screens' composer, as the app's (and the phone discussion tab's): its own padding, no bar. */ phone?: boolean }) {
  const info = useEntityThread(type, entityId);
  const convId = info.data?.conversation_id;
  if (!convId || !info.data?.can_post) return null;
  if (phone) return <div className="pt-1" data-testid="pinned-composer"><Composer key={convId} conversationId={convId} placeholder={placeholder} /></div>;
  return (
    <ThreadPrefsProvider conversationId={convId} canReply>
      <div className="[&_form]:!p-0" data-testid="pinned-composer"><Composer key={convId} conversationId={convId} placeholder={placeholder} /></div>
    </ThreadPrefsProvider>
  );
}
